import {
    attach,
    createEffect,
    createEvent,
    createStore,
    type EventPayload,
    type StoreValue,
    sample,
    split,
} from 'effector'
import { produce } from 'immer'
import { omit, uniq } from 'lodash'
import { debounce, spread } from 'patronum'

import * as chat from '@/entities/chat'
import { env } from '@/shared/config'
import { assertUnreachable } from '@/shared/lib/types'

import { chatapi, type MessageReactionRequest } from '../api'
import * as channelModel from './model.channel'
import * as connModel from './model.conn'
import * as userModel from './model.user'

export const reactionSelected = createEvent<{ messageId: string; emoji: string }>()

// message_id -> emoji -> user_id[]
export const $reactionByMessageId = createStore<Record<string, Record<string, string[]>>>({})

type ReactionQueueItem = MessageReactionRequest & { op: 'add' | 'remove' }

// "to-be-sent" reactions queue
// message_id|emoji => reaction
const $reactionQueue = createStore<Record<string, ReactionQueueItem>>({})

const messageReactionAddFx = attach({ effect: chatapi.messageReactionAdd })
const messageReactionRemoveFx = attach({ effect: chatapi.messageReactionRemove })
const messageReactionFx = createEffect<ReactionQueueItem, void>()

const reaction = sample({
    clock: reactionSelected,
    source: {
        reactions: $reactionByMessageId,
        channel: channelModel.$chatChannel,
        user: chat.$userInfo,
    },
    filter: userModel.$canReactToMessages,
    fn: ({ reactions, channel, user }, selected) => ({
        reactions,
        message_id: selected.messageId,
        emoji: selected.emoji,
        channel,
        user_id: user!.user_id,
    }),
})

const reactionToggle = split(reaction, {
    added: (event) => {
        const users = event.reactions[event.message_id]?.[event.emoji] ?? []
        return users.indexOf(event.user_id) === -1
    },
    removed: () => true,
})

sample({
    clock: reactionToggle.added,
    source: $reactionQueue,
    fn: (queue, payload) => enqueueReaction('add', queue, payload),
    target: $reactionQueue,
})

sample({
    clock: reactionToggle.removed,
    source: $reactionQueue,
    fn: (queue, payload) => enqueueReaction('remove', queue, payload),
    target: $reactionQueue,
})

export const reactionQueueTick = createEvent()
debounce({
    source: env.IS_TEST ? createEvent() : $reactionQueue,
    timeout: 500,
    target: reactionQueueTick,
})

// checkout reaction queue and make requests
// NOTE: this is a good place to batch requests in the future
sample({
    clock: reactionQueueTick,
    source: $reactionQueue,
    // skip tick if queue is empty
    filter: (queue) => Object.keys(queue).length > 0,
    fn: (queue) => {
        const [key, reaction] = Object.entries(queue)[0]!

        return {
            queue: omit(queue, key),
            reaction,
        }
    },
    target: spread({
        targets: {
            queue: $reactionQueue,
            reaction: messageReactionFx,
        },
    }),
})

messageReactionFx.use((queued) => {
    const { op, ...reaction } = queued

    if (op === 'add') {
        return messageReactionAddFx(reaction)
    }

    if (op === 'remove') {
        return messageReactionRemoveFx(reaction)
    }

    assertUnreachable(op)
})

// optimistic update

sample({
    clock: reactionToggle.added,
    source: $reactionByMessageId,
    fn: addReaction,
    target: $reactionByMessageId,
})

sample({
    clock: reactionToggle.removed,
    source: $reactionByMessageId,
    fn: removeReaction,
    target: $reactionByMessageId,
})

// channel events
sample({
    clock: connModel.chatEventReceived,
    source: {
        reactions: $reactionByMessageId,
        user: chat.$userInfo,
    },
    fn: ({ reactions, user }, { event }) => {
        if (event.type === 'message_reaction_added') {
            // ignore own reactions to prevent flickering
            if (event.payload.user_id === user?.user_id) {
                return reactions
            }

            return addReaction(reactions, event.payload)
        }

        if (event.type === 'message_reaction_removed') {
            // ignore own reactions to prevent flickering
            if (event.payload.user_id === user?.user_id) {
                return reactions
            }

            return removeReaction(reactions, event.payload)
        }

        return reactions
    },
    target: $reactionByMessageId,
})

type Reaction = {
    message_id: string
    emoji: string
    user_id: string
}

function addReaction(reactions: Record<string, Record<string, string[]>>, { message_id, emoji, user_id }: Reaction) {
    return {
        ...reactions,
        [message_id]: {
            ...reactions[message_id],
            [emoji]: uniq([...(reactions[message_id]?.[emoji] ?? []), user_id]),
        },
    }
}

function removeReaction(reactions: Record<string, Record<string, string[]>>, { message_id, emoji, user_id }: Reaction) {
    const users = reactions[message_id]?.[emoji] ?? []
    const newUsers = users.filter((id) => id !== user_id)

    return {
        ...reactions,
        [message_id]: {
            ...reactions[message_id],
            [emoji]: newUsers,
        },
    }
}

// Sync reactions with history
const historyReceived = channelModel.channelHistoryFx.done

sample({
    clock: historyReceived,
    source: {
        reactions: $reactionByMessageId,
    },
    fn: ({ reactions }, { result: history }) => {
        const historyReactions = history.reaction_by_message_id

        if (!historyReactions) {
            return reactions
        }

        const newReactions = { ...reactions }

        for (const [message_id, reactions] of Object.entries(historyReactions)) {
            for (const [emoji, users] of Object.entries(reactions)) {
                newReactions[message_id] = {
                    ...newReactions[message_id],
                    [emoji]: users,
                }
            }
        }

        return newReactions
    },
    target: $reactionByMessageId,
})

function enqueueReaction(
    op: 'add' | 'remove',
    queue: StoreValue<typeof $reactionQueue>,
    payload: EventPayload<typeof reaction>
): typeof queue {
    const reactionKey = getReactionKey(payload)
    const oppositeOp = op === 'add' ? 'remove' : 'add'
    if (queue[reactionKey]?.op === oppositeOp) {
        // remove opposite operation from queue (e.g. user add reaction and then remove it before tick)
        return omit(queue, reactionKey)
    }

    return {
        ...queue,
        [reactionKey]: {
            op,
            message_id: payload.message_id,
            emoji: payload.emoji,
            channel: payload.channel,
        },
    }
}

function getReactionKey(params: { message_id: string; emoji: string }): string {
    return `${params.message_id}|${params.emoji}`
}

// -- Hide all user's reactions in every channel

sample({
    clock: connModel.messageHiddenAll,
    source: $reactionByMessageId,
    fn: (reactions, { user_id }) =>
        produce(reactions, (reactions) => {
            for (const messageReactions of Object.values(reactions)) {
                for (const [emoji, users] of Object.entries(messageReactions)) {
                    messageReactions[emoji] = users.filter((id) => id !== user_id)
                }
            }
        }),
    target: $reactionByMessageId,
})
